import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import Label from "../../../../shared/ui/form/Label";
import Input from "../../../../shared/ui/form/input/InputField";
import Button from "../../../../shared/ui/button/Button";
import { sendPasswordResetEmail } from "../../application/passwordReset";
import { motion } from "framer-motion";
import { Mail, AlertCircle, CheckCircle2 } from "lucide-react";

export default function ResetPasswordForm() {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);
    setLoading(true);

    const form = e.target as HTMLFormElement;
    const email = (form.elements.namedItem("email") as HTMLInputElement).value;

    try {
      await sendPasswordResetEmail(email);
      setSuccessMessage(t("auth.resetLinkSent"));
    } catch {
      setErrorMessage(t("auth.errorReset"));
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" as const }
    }
  };

  return (
    <div className="w-full">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="space-y-8"
      >
        <motion.div variants={itemVariants}>
          <h1 className="text-3xl font-bold text-content-primary mb-2">
            {t("auth.resetPasswordTitle")}
          </h1>
          <p className="text-content-tertiary">
            {t("auth.resetPasswordSubtitle")}
          </p>
        </motion.div>

        <form onSubmit={handleResetPassword}>
          <motion.div variants={containerVariants} className="space-y-6">
            {errorMessage && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="p-4 rounded-2xl bg-error-50 dark:bg-error-500/10 border border-error-200 dark:border-error-500/20 text-error-600 dark:text-error-400 text-sm flex gap-3 items-center"
              >
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                {errorMessage}
              </motion.div>
            )}

            {successMessage && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="p-4 rounded-2xl bg-success-50 dark:bg-success-500/10 border border-success-200 dark:border-success-500/20 text-success-600 dark:text-success-400 text-sm flex gap-3 items-center"
              >
                <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
                {successMessage}
              </motion.div>
            )}

            <motion.div variants={itemVariants} className="space-y-2">
              <Label htmlFor="email">
                {t("auth.email")} <span className="text-error-500">*</span>
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-tertiary" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  className="pl-10"
                  placeholder={t("auth.enterEmail")}
                  required
                />
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="pt-4">
              <Button
                type="submit"
                className="w-full"
                loading={loading}
              >
                {t("auth.sendResetLink")}
              </Button>
            </motion.div>
          </motion.div>
        </form>

        <motion.div
          variants={itemVariants}
          className="pt-8 border-t border-gray-100 dark:border-white/[0.05]"
        >
          <p className="text-sm font-medium text-center text-content-tertiary">
            <Link
              to="/login"
              className="text-brand-600 dark:text-brand-400 font-bold hover:underline ml-1"
            >
              {t("auth.backToLogin")}
            </Link>
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}
